// Geometric face-shape classifier — zero extra ML weight (pure ratios off the
// landmarks MediaPipe already gives us). Maps to recommended frame styles so
// the PDP can nudge the shopper, mirroring Lenskart/FittingBox recommendations.
//
// Heuristic, not clinical: we surface it as guidance, not a verdict.

import { IDX, type LM } from './landmarks';

export type FaceShape = 'oval' | 'round' | 'square' | 'heart' | 'oblong';

export interface FaceShapeResult {
  shape: FaceShape;
  /** Recommended frame styles (i18n keys live in the UI layer). */
  recommends: string[];
}

function px(a: LM, b: LM, w: number, h: number): number {
  return Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);
}

// Forehead width proxy: temple-to-temple high on the face.
const FOREHEAD_L = 21;
const FOREHEAD_R = 251;
// Jaw width proxy: gonion-to-gonion low on the face.
const JAW_L = 172;
const JAW_R = 397;

const RECOMMENDATIONS: Record<FaceShape, string[]> = {
  oval: ['square', 'rectangular', 'aviator', 'wayfarer'],
  round: ['square', 'rectangular', 'geometric'],
  square: ['round', 'oval', 'aviator', 'cat-eye'],
  heart: ['round', 'oval', 'rimless', 'cat-eye'],
  oblong: ['oversized', 'round', 'wayfarer'],
};

/**
 * Classify from a single frame. Cheap enough to run every frame, but the engine
 * only samples it occasionally and reports the modal result.
 */
export function classifyFaceShape(lm: LM[], w: number, h: number): FaceShapeResult {
  const faceLength = px(lm[IDX.foreheadTop], lm[IDX.chin], w, h);
  const cheekWidth = px(lm[IDX.leftCheek], lm[IDX.rightCheek], w, h);
  const foreheadWidth = px(lm[FOREHEAD_L], lm[FOREHEAD_R], w, h);
  const jawWidth = px(lm[JAW_L], lm[JAW_R], w, h);

  const lengthRatio = faceLength / cheekWidth; // >1.45 → long face
  const jawRatio = jawWidth / foreheadWidth; // >1 → wide jaw, <1 → narrow jaw
  const foreheadVsCheek = foreheadWidth / cheekWidth;

  let shape: FaceShape;
  if (lengthRatio >= 1.5) {
    shape = 'oblong';
  } else if (jawRatio < 0.82 && foreheadVsCheek > 0.92) {
    shape = 'heart'; // wide forehead, narrow jaw
  } else if (lengthRatio <= 1.18) {
    // Short face: square if jaw is wide/angular, round otherwise.
    shape = jawRatio > 0.93 ? 'square' : 'round';
  } else {
    shape = 'oval';
  }

  return { shape, recommends: RECOMMENDATIONS[shape] };
}

/** Tracks the modal shape across samples so the UI doesn't flicker. */
export class FaceShapeVoter {
  private counts = new Map<FaceShape, number>();
  add(shape: FaceShape) {
    this.counts.set(shape, (this.counts.get(shape) ?? 0) + 1);
  }
  reset() {
    this.counts.clear();
  }
  result(): FaceShapeResult | null {
    let best: FaceShape | null = null;
    let bestN = 0;
    let total = 0;
    for (const [shape, n] of this.counts) {
      total += n;
      if (n > bestN) {
        best = shape;
        bestN = n;
      }
    }
    if (!best || total < 8) return null; // need a few votes first
    return { shape: best, recommends: RECOMMENDATIONS[best] };
  }
}
